/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef, useState} from 'react'
import {KTIcon, toAbsoluteUrl} from '../../../../../_metronic/helpers'
import {useAuth} from '../../../../modules/auth'
import {AssetPageTitle} from '../../../../../_metronic/layout/core/AssetPageData'
import {ToastContainer} from 'react-toastify'
import {saveAs} from 'file-saver'
import ExcelJS from 'exceljs'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import Searchbar from 'app/components/Searchbar'
import ExportButton from 'app/components/ExportButton'
import ConfirmationModal from 'app/components/ConfirmationModal'
import AssetDataService from '../../services/asset.services'
import EmptyState from 'app/components/EmptyState'
import StatusIcon from 'app/components/StatusIcon'
import OwnerDisplay from 'app/components/OwnerDisplay'

const AssetRepository: React.FC = () => {
  const {currentUser} = useAuth()
  const [repoTemplates, setrepoTemplates] = useState<any[]>([])
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [searchLength, setSearchLength] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [checkedItems, setCheckedItems] = useState<any[]>([])
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const overlayRef = useRef<HTMLElement>(null)
  const [show, setShow] = useState(false)
  const [personnels, setPersonnels] = useState<any[]>([])
  const [showOwnerPopover, setShowOwnerPopover] = useState(false)
  const [selectedOwner, setSelectedOwner] = useState('')
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isTogglingScope, setIsTogglingScope] = useState(false)

  useEffect(() => {
    AssetDataService.getRepositories(currentUser, setrepoTemplates)
  }, [currentUser])

  useEffect(() => {
    setFilteredItems(repoTemplates)
  }, [repoTemplates])

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredItems(repoTemplates)
      setSearchLength(false)
    } else {
      const filtered = repoTemplates.filter((item) => {
        const repoName = (item.repo_name || '').toLowerCase()
        return repoName.includes(searchTerm)
      })
      setFilteredItems(filtered)
      setSearchLength(filtered.length === 0)
    }
  }, [repoTemplates, searchTerm])

  useEffect(() => {
    AssetDataService.getEmployees(currentUser, setPersonnels)
  }, [currentUser])

  const handleDeselectClick = () => {
    setCheckedItems([])
    setSelectAll(false)
  }

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target as Node) && show) {
        setShow(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [show])

  const handleSelectAllChange = (e) => {
    setSelectAll(e.target.checked)
    if (e.target.checked) {
      setCheckedItems(filteredItems.map((item) => item.id))
    } else {
      setCheckedItems([])
    }
  }

  const handleCheckChange = (e) => {
    const {checked, value} = e.target
    setCheckedItems((prev) => (checked ? [...prev, value] : prev.filter((item) => item !== value)))
  }

  const getAuditScopeStatus = () => {
    const selectedItems = filteredItems.filter((item) => checkedItems.includes(item.id))
    if (selectedItems.length === 0) return null

    const allInScope = selectedItems.every((item) => item.audit_scope === true)
    const allOutOfScope = selectedItems.every((item) => item.audit_scope === false)

    if (allInScope) return 'in'
    if (allOutOfScope) return 'out'
    return 'out'
  }

  const auditScopeStatus = getAuditScopeStatus()

  const handleToggleAuditScope = async () => {
    try {
      const newScopeValue = auditScopeStatus === 'in' ? false : true
      await AssetDataService.updateRepositoryAuditScope(checkedItems, newScopeValue, currentUser)
      showSuccessToast({
        message: `Repository marked ${newScopeValue ? 'in' : 'out of'} audit scope`,
      })

      setCheckedItems([])
      setSelectAll(false)
    } catch (error) {
      console.error('Error toggling audit scope:', error)
    }
  }

  const handleAssignOwner = async () => {
    try {
      await AssetDataService.assignRepositoryOwner(checkedItems, selectedOwner, currentUser)
      showSuccessToast({message: 'Owners assigned successfully'})
      setCheckedItems([])
      setSelectAll(false)
      setShowOwnerPopover(false)
      setSelectedOwner('')
    } catch (error) {
      showErrorToast({message: 'Failed to assign owners'})
      console.error('Error assigning owner:', error)
    }
  }

  const exportData = async () => {
    try {
      const data = await AssetDataService.exportRepositories(currentUser)

      if (data.length > 0) {
        const fields = [
          {key: 'repo_name', header: 'Repository Name', width: 25},
          {key: 'repo_link', header: 'Repository URL', width: 30},
          {key: 'platform', header: 'Platform', width: 15},
          {key: 'visibility', header: 'Visibility', width: 15},
          {key: 'description', header: 'Description', width: 40},
          {key: 'language', header: 'Primary Language', width: 20},
          {key: 'default_branch', header: 'Default Branch', width: 15},
          {key: 'size', header: 'Size (KB)', width: 12},
          {key: 'stars', header: 'Stars', width: 10},
          {key: 'forks', header: 'Forks', width: 10},
          {key: 'watchers', header: 'Watchers', width: 10},
          {key: 'open_issues', header: 'Open Issues', width: 12},
          {key: 'created_at', header: 'Created At', width: 20},
          {key: 'updated_at', header: 'Updated At', width: 20},
          {key: 'pushed_at', header: 'Last Pushed At', width: 20},
          {key: 'createdAt', header: 'Added to System', width: 20},
          {key: 'integration_owner', header: 'Integration Owner', width: 20},
          {key: 'audit_scope', header: 'Audit Scope', width: 15},
        ]

        const workbook = new ExcelJS.Workbook()
        const worksheet = workbook.addWorksheet('Repositories')

        worksheet.columns = fields.map((field) => ({
          header: field.header,
          key: field.key,
          width: field.width,
        }))

        const headerRow = worksheet.getRow(1)
        headerRow.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 11}
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: {argb: 'FF000000'},
        }
        headerRow.height = 30
        headerRow.alignment = {horizontal: 'center', vertical: 'middle'}
        worksheet.views = [{state: 'frozen', ySplit: 1}]

        data.forEach((row, index) => {
          const rowData = fields.reduce((acc, field) => {
            let value = row[field.key]

            // Handle special cases
            if (['created_at', 'updated_at', 'pushed_at'].includes(field.key) && value) {
              const date = new Date(value)
              acc[field.key] = date.toLocaleString()
            } else if (field.key === 'createdAt' && value) {
              const date = value.toDate ? value.toDate() : new Date(value)
              acc[field.key] = date.toLocaleString()
            } else if (field.key === 'repo_link') {
              acc[field.key] = {text: value || '', hyperlink: value || ''}
            } else if (['stars', 'forks', 'watchers', 'open_issues', 'size'].includes(field.key)) {
              acc[field.key] = Number(value) || 0
            } else if (field.key === 'audit_scope') {
              acc[field.key] = value === true ? 'Yes' : 'No'
            } else {
              acc[field.key] = value || ''
            }

            return acc
          }, {})

          rowData['serial_number'] = index + 1

          const addedRow = worksheet.addRow(rowData)
          addedRow.height = 20
          addedRow.font = {size: 11}

          fields.forEach((field) => {
            const cell = addedRow.getCell(field.key)
            if (field.key === 'audit_scope') {
              if (cell.value === 'Yes') {
                cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: {argb: 'D9EAD3'},
                }
              } else if (cell.value === 'No') {
                cell.fill = {
                  type: 'pattern',
                  pattern: 'solid',
                  fgColor: {argb: 'F4CCCC'},
                }
              }
            }
          })
        })

        const buffer = await workbook.xlsx.writeBuffer()
        const excelBlob = new Blob([buffer], {type: 'application/octet-stream'})
        saveAs(excelBlob, 'Repositories.xlsx')
        showSuccessToast({message: 'Repository data exported successfully'})
      } else {
        showErrorToast({message: 'No repository data found to export'})
      }
    } catch (error) {
      console.error('Error exporting repositories:', error)
      showErrorToast({message: 'Error exporting repository data'})
    }
  }

  return (
    <>
      <AssetPageTitle
        icon='Icon-1.svg'
        title='Asset Management'
        desc='Monitor your devices and repositories that are within your audit scope'
        // breadcrumbs={usersBreadcrumbs}
      >
        Asset Management
      </AssetPageTitle>

      <div className='card card-custom '>
        <div className='card-header border-0 pt-6 h-100'>
          <div className='card-title'>
            {/* begin::Search */}
            <div className='flex-lg-fill'>
              <Searchbar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            </div>
          </div>
          <div className='card-toolbar'>
            {/* begin::Group actions */}
            {checkedItems.length > 0 ? (
              <div className='d-flex gap-2'>
                {checkedItems.length > 0 ? (
                  <div className='  border-end pe-4 text-center' style={{color: '#A1A5B7'}}>
                    <span>
                      <p className='pt-4'>{checkedItems.length} Selected</p>
                    </span>
                  </div>
                ) : null}
                <div style={{display: 'flex', alignItems: 'center'}}>
                  <div
                    className='me-4 ms-2'
                    style={{cursor: 'pointer'}}
                    onClick={handleDeselectClick}
                  >
                    <img src='/media/buttons/Deselect.svg' alt='Deselect' />
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-light d-flex align-items-center fs-6 me-4'
                    style={{
                      height: '40px',
                      background: '#fff',
                      color: '#000',
                      border: '1px solid #C5C5C5',
                    }}
                    onClick={() => setShowConfirmModal(true)}
                  >
                    <KTIcon
                      iconName={auditScopeStatus === 'in' ? 'cross-circle' : 'check-circle'}
                      className='fs-2 me-1'
                    />
                    {auditScopeStatus === 'in' ? 'Mark Out of Scope' : 'Mark In Audit Scope'}
                  </button>
                  <div className='position-relative'>
                    <button
                      type='button'
                      className='btn btn-sm fs-6'
                      style={{height: '40px', background: '#0E9F6E', color: '#fff'}}
                      onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#148363')
                      }
                      onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                        (e.currentTarget.style.backgroundColor = '#0E9F6E')
                      }
                      onClick={() => setShowOwnerPopover(!showOwnerPopover)}
                    >
                      Assign Owner
                    </button>
                    {showOwnerPopover && (
                      <div
                        className='position-absolute bg-white rounded shadow-sm p-4'
                        style={{
                          width: '250px',
                          zIndex: 1000,
                          right: '0',
                          top: '100%',
                          marginTop: '8px',
                          boxShadow: '0px 0px 50px 0px rgba(82, 63, 105, 0.15)',
                        }}
                      >
                        <select
                          className='form-select mb-3'
                          value={selectedOwner}
                          onChange={(e) => setSelectedOwner(e.target.value)}
                        >
                          <option value=''>Select Owner</option>
                          {personnels
                            .filter((user) => user.role === 'Admin' || user.role === 'Owner')
                            .map((personnel) => (
                              <option key={personnel.id} value={personnel.full_name}>
                                {personnel.full_name}
                              </option>
                            ))}
                        </select>
                        <div className='d-flex justify-content-end gap-2'>
                          <button
                            className='btn btn-sm btn-light'
                            onClick={() => {
                              setShowOwnerPopover(false)
                              setSelectedOwner('')
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className='btn btn-sm fs-6'
                            style={{height: '35px', background: '#0E9F6E', color: '#fff'}}
                            onMouseEnter={(e: React.MouseEvent<HTMLElement>) =>
                              (e.currentTarget.style.backgroundColor = '#148363')
                            }
                            onMouseLeave={(e: React.MouseEvent<HTMLElement>) =>
                              (e.currentTarget.style.backgroundColor = '#0E9F6E')
                            }
                            onClick={handleAssignOwner}
                            disabled={!selectedOwner}
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <ExportButton className='me-4' onClick={exportData} />
            )}
            {/* end::Group actions */}
          </div>
        </div>
        <div className='card-body py-3 '>
          <div className='table-responsive' style={{minHeight: '60vh', overflowY: 'auto'}}>
            {/* begin::Table */}
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
              {/* begin::Table head */}
              <thead>
                <tr className='fw-bold fs-7' style={{color: '#5A5A5A'}}>
                  <th className='w-25px'>
                    <div className='form-check form-check-sm form-check-custom form-check-solid'>
                      <input
                        className='form-check-input'
                        type='checkbox'
                        checked={selectAll}
                        data-kt-check='true'
                        data-kt-check-target='.widget-9-check'
                        onChange={handleSelectAllChange}
                      />
                    </div>
                  </th>

                  <th className='min-w-300px'>REPOSITORY NAME</th>
                  <th className='min-w-200px'>PLATFORM</th>
                  <th className='min-w-200px'>VISIBILITY</th>
                  <th className='min-w-150px'>OWNER</th>
                  <th className='min-w-150px'>IN AUDIT SCOPE</th>
                  {/* <th className='min-w-100px'></th> */}
                </tr>
              </thead>
              {/* end::Table head */}
              {/* begin::Table body */}
              <tbody>
                {filteredItems && filteredItems.length > 0 ? (
                  filteredItems.map((risk) => (
                    <tr key={risk.id}>
                      <td>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input
                            className='form-check-input widget-9-check'
                            type='checkbox'
                            value={risk.id}
                            checked={selectAll || checkedItems.includes(risk.id)}
                            onChange={handleCheckChange}
                          />
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center fw-bold fs-6 py-2'>
                          {risk.repo_link
                            ? risk.repo_link.replace(/^https?:\/\/[^\/]+\//, '')
                            : risk.repo_name}
                        </div>
                      </td>

                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <div className='d-flex align-items-center'>
                              <img
                                src={toAbsoluteUrl(`/media/integrations/${risk.platform}.svg`)}
                                alt={risk.platform}
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  marginRight: '8px',
                                }}
                              />
                              <span className='text-dark fw-semibold fs-6'>{risk.platform}</span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <div className='fw-semibold fs-6'>{risk.visibility}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start  align-items-center'>
                            {risk.integration_owner ? (
                              <OwnerDisplay owner={risk.integration_owner} />
                            ) : (
                              <span className='fw-semibold fs-6'>--</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex align-items-center'>
                          <div className='d-flex justify-content-start flex-column'>
                            <StatusIcon value={risk.audit_scope} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <>
                    <tr style={{height: '100%'}}>
                      <td colSpan={10}>
                        <div className='d-flex justify-content-center align-items-center text-center' style={{height: '100%'}}>
                          {searchLength ? (
                            <EmptyState
                              title="No repositories found!"
                              description="We couldn't find any repositories that match your search. Maybe try a different search!"
                            />
                          ) : (
                            <EmptyState
                              title="No Repositories Found"
                              description="Repositories will appear here once you connect your integration."
                            />
                          )}
                        </div>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
              {/* end::Table body */}
            </table>
            {/* end::Table */}
          </div>
        </div>
      </div>
      {/* begin::Table container */}
      <ToastContainer />
      {/* begin::Body */}
      <ConfirmationModal
        isModalOpen={showConfirmModal}
        hideModal={() => setShowConfirmModal(false)}
        confirmBtnClickHandler={async () => {
          setIsTogglingScope(true)
          await handleToggleAuditScope()
          setIsTogglingScope(false)
          setShowConfirmModal(false)
        }}
        isLoading={isTogglingScope}
        title={auditScopeStatus === 'in' ? 'Mark Out of Audit Scope?' : 'Mark In Audit Scope?'}
        message={
          auditScopeStatus === 'in'
            ? 'Are you sure you want to mark the selected repositories out of audit scope?'
            : 'Are you sure you want to mark the selected repositories in audit scope?'
        }
        confirmButtonText={auditScopeStatus === 'in' ? 'Mark Out of Scope' : 'Mark In Audit Scope'}
        type='default'
      />
    </>
  )
}

export {AssetRepository}
